import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminClientePerfil, getColorHex, isColorLight } from '../../../hooks/useAdminClientePerfil';
import type { Prenda } from '../../../hooks/useClienteArmario';
import type { Recomendacion } from '../../../hooks/useClienteRecomendaciones';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';
import { generarLooksSugeridos } from '../../../lib/looksSugeridos';

interface DashboardProps {
  prendas: Prenda[];
  recomendaciones: Recomendacion[];
  calendario: LookCalendario[];
  clienteId?: number;
}

export default function Dashboard({ prendas, recomendaciones, calendario, clienteId }: DashboardProps) {
  const { perfil: adminPerfil } = useAdminClientePerfil(clienteId);

  // Generate looks using same logic as LooksSugeridos tab
  const looksSugeridos = useMemo(() => {
    const paleta = adminPerfil?.paleta_colores ?? [];
    const ocasiones = adminPerfil?.ocasiones_preferidas ?? ['Diario'];
    return generarLooksSugeridos(prendas, paleta, ocasiones);
  }, [prendas, adminPerfil]);

  const favoritosIds = adminPerfil?.looks_favoritos ?? [];
  const looksFavoritos = useMemo(() => {
    return looksSugeridos.filter((l) => favoritosIds.includes(l.id));
  }, [looksSugeridos, favoritosIds]);

  const stats = useMemo(() => {
    const total = prendas.length;
    const activas = prendas.filter((p) => p.activa).length;
    const inactivas = total - activas;
    const totalUsos = prendas.reduce((sum, p) => sum + p.frecuencia_uso, 0);
    const promedioUsos = total > 0 ? Math.round(totalUsos / total) : 0;
    const topPrenda = [...prendas].sort((a, b) => b.frecuencia_uso - a.frecuencia_uso)[0];
    const sinUsar = prendas.filter((p) => p.frecuencia_uso === 0).length;
    const proximosLooks = calendario
      .filter((c) => c.estado === 'planeado')
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
      .slice(0, 3);
    const nuevasRecomendaciones = recomendaciones.filter((r) => !r.leida).length;

    // Looks pendientes de acción del cliente
    const looksUsados = calendario.filter((c) => c.estado === 'usado');
    const looksSinFoto = looksUsados.filter((c) => !c.foto_real_url).length;
    const looksSinValorar = looksUsados.filter((c) => !c.calificacion).length;

    // Valoración promedio global del cliente
    const looksConValoracion = looksUsados.filter((c) => c.calificacion != null);
    const avgValoracion = looksConValoracion.length > 0
      ? Math.round(
          (looksConValoracion.reduce((sum, l) => sum + (l.calificacion ?? 0), 0) /
            looksConValoracion.length) *
            10
        ) / 10
      : 0;

    return {
      total,
      activas,
      inactivas,
      totalUsos,
      promedioUsos,
      topPrenda,
      sinUsar,
      proximosLooks,
      nuevasRecomendaciones,
      looksSinFoto,
      looksSinValorar,
      avgValoracion,
      totalValorados: looksConValoracion.length,
    };
  }, [prendas, calendario, recomendaciones]);

  const hasPendientes = stats.looksSinFoto > 0 || stats.looksSinValorar > 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-brand-black text-3xl font-light">
          Tu espacio exclusivo
        </h1>
        <p className="font-sans text-brand-gray-500 text-sm mt-1">
          Panel de control de tu asesoría de imagen personal
        </p>
      </div>

      {/* Favoritos destacados */}
      {looksFavoritos.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center text-red-500">
                <i className="ri-heart-3-fill" />
              </div>
              <h3 className="font-sans text-brand-black text-sm font-medium">
                Tus looks favoritos
              </h3>
            </div>
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'looks-sugeridos' });
                window.dispatchEvent(navEvent);
              }}
              className="font-sans text-brand-gray-500 text-xs hover:text-brand-black transition-colors"
            >
              Ver todos →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {looksFavoritos.slice(0, 3).map((look) => (
              <div
                key={look.id}
                className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col transition-shadow hover:shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-brand-black text-lg font-medium">{look.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-brand-sage/20 font-sans text-[11px] text-brand-black">
                        {look.ocasion}
                      </span>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-brand-gray-100 font-sans text-[11px] text-brand-gray-600">
                        {look.temporada}
                      </span>
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                    <i className="ri-heart-3-fill text-sm" />
                  </div>
                </div>

                {/* Collage de prendas */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {look.prendas.map((prenda) => (
                    <div
                      key={prenda.id}
                      className="relative rounded-xl overflow-hidden bg-brand-gray-50 aspect-[3/4]"
                    >
                      {prenda.imagen_url ? (
                        <img
                          src={prenda.imagen_url}
                          alt={prenda.nombre}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                          <i className="ri-t-shirt-line text-2xl" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="font-sans text-white text-xs font-medium truncate">{prenda.nombre}</p>
                        <p className="font-sans text-white/70 text-[10px]">{prenda.categoria}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Lista de prendas con colores */}
                <div className="flex-1 space-y-2">
                  {look.prendas.map((prenda) => {
                    const hex = getColorHex(prenda.color);
                    return (
                      <div key={prenda.id} className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                          style={{ backgroundColor: hex }}
                        />
                        <span className="font-sans text-sm text-brand-gray-700 truncate">
                          {prenda.nombre}
                        </span>
                        <span className="font-sans text-[11px] text-brand-gray-400 ml-auto shrink-0">
                          {prenda.categoria}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-auto pt-3 border-t border-brand-gray-200">
                  <Link
                    to="/mi-asesoria"
                    onClick={() => {
                      const navEvent = new CustomEvent('navigate-backoffice', { detail: 'looks-sugeridos' });
                      window.dispatchEvent(navEvent);
                    }}
                    className="block text-center font-sans text-brand-black text-sm font-medium hover:text-brand-gray-600 transition-colors py-2"
                  >
                    Ir a looks sugeridos →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paleta de colores definida por Anthony */}
      {adminPerfil?.paleta_colores && adminPerfil.paleta_colores.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center text-brand-sage">
              <i className="ri-palette-line" />
            </div>
            <h3 className="font-sans text-brand-black text-sm font-medium">
              Tu paleta de colores recomendada
            </h3>
            <span className="font-sans text-brand-gray-400 text-[10px] ml-auto uppercase tracking-wider">
              Definido por Anthony
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {adminPerfil.paleta_colores.map((color) => {
              const hex = getColorHex(color);
              const light = isColorLight(hex);
              return (
                <span
                  key={color}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs"
                  style={{ backgroundColor: hex + '20' }}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  <span className={light ? 'text-brand-black' : 'text-brand-black'}>{color}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Notificaciones / pendientes */}
      {hasPendientes && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500">
              <i className="ri-notification-3-line" />
            </div>
            <h3 className="font-sans text-brand-black text-sm font-medium">
              Tienes acciones pendientes
            </h3>
          </div>

          {stats.looksSinFoto > 0 && (
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'historial' });
                window.dispatchEvent(navEvent);
              }}
              className="flex items-center gap-3 p-3 rounded-xl bg-brand-cream hover:bg-brand-sage/20 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-brand-black text-white flex items-center justify-center font-sans text-xs font-medium shrink-0">
                {stats.looksSinFoto}
              </div>
              <div>
                <p className="font-sans text-brand-black text-sm font-medium">
                  {stats.looksSinFoto === 1 ? 'Look sin foto real' : `${stats.looksSinFoto} looks sin foto real`}
                </p>
                <p className="font-sans text-brand-gray-500 text-xs">
                  Sube una foto del outfit que usaste para tu historial
                </p>
              </div>
              <i className="ri-arrow-right-s-line text-brand-gray-400 ml-auto" />
            </Link>
          )}

          {stats.looksSinValorar > 0 && (
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'historial' });
                window.dispatchEvent(navEvent);
              }}
              className="flex items-center gap-3 p-3 rounded-xl bg-brand-cream hover:bg-brand-sage/20 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-sans text-xs font-medium shrink-0">
                {stats.looksSinValorar}
              </div>
              <div>
                <p className="font-sans text-brand-black text-sm font-medium">
                  {stats.looksSinValorar === 1 ? 'Look sin valorar' : `${stats.looksSinValorar} looks sin valorar`}
                </p>
                <p className="font-sans text-brand-gray-500 text-xs">
                  Evalúa cómo funcionó la combinación para mejorar futuros looks
                </p>
              </div>
              <i className="ri-arrow-right-s-line text-brand-gray-400 ml-auto" />
            </Link>
          )}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-t-shirt-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">
            {stats.total}
          </p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">
            Prendas registradas
          </p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-bar-chart-grouped-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">
            {stats.totalUsos}
          </p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">
            Usos registrados
          </p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-amber-500 mb-3">
            <i className="ri-star-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">
            {stats.avgValoracion > 0 ? stats.avgValoracion : '-'}
          </p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">
            Valoración media ({stats.totalValorados} valorados)
          </p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-lightbulb-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">
            {stats.nuevasRecomendaciones}
          </p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">
            Recomendaciones nuevas
          </p>
        </div>
      </div>

      {/* Top prenda + alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prenda más usada */}
        {stats.topPrenda && (
          <div className="bg-brand-cream rounded-2xl p-6 border border-brand-gray-200">
            <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium">
              Prenda más utilizada
            </h3>
            <div className="mt-4 flex items-start gap-4">
              {stats.topPrenda.imagen_url ? (
                <div className="w-20 h-24 rounded-lg overflow-hidden bg-white flex-shrink-0">
                  <img
                    src={stats.topPrenda.imagen_url}
                    alt={stats.topPrenda.nombre}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ) : (
                <div className="w-20 h-24 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center text-brand-gray-400">
                    <i className="ri-image-line text-lg" />
                  </div>
                </div>
              )}
              <div>
                <p className="font-serif text-brand-black text-lg">
                  {stats.topPrenda.nombre}
                </p>
                <p className="font-sans text-brand-gray-500 text-sm mt-1">
                  {stats.topPrenda.categoria} · {stats.topPrenda.color}
                </p>
                <p className="font-sans text-brand-sage text-sm font-medium mt-2">
                  {stats.topPrenda.frecuencia_uso} usos registrados
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Prendas sin uso */}
        <div className="bg-white rounded-2xl p-6 border border-brand-gray-200">
          <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium">
            Prendas sin uso
          </h3>
          {stats.sinUsar > 0 ? (
            <div className="mt-4">
              <p className="font-serif text-brand-black text-3xl font-light">
                {stats.sinUsar}
              </p>
              <p className="font-sans text-brand-gray-500 text-sm mt-2">
                Tienes {stats.sinUsar} {stats.sinUsar === 1 ? 'prenda' : 'prendas'} que no has registrado como usada en el sistema. Considera si realmente las necesitas o si deberías integrarlas en tu rotación.
              </p>
              <Link
                to="/mi-asesoria"
                onClick={() => {
                  const navEvent = new CustomEvent('navigate-backoffice', { detail: 'armario' });
                  window.dispatchEvent(navEvent);
                }}
                className="mt-4 inline-block font-sans text-brand-black text-sm font-medium hover:text-brand-gray-600 transition-colors"
              >
                Revisar armario →
              </Link>
            </div>
          ) : (
            <p className="font-sans text-brand-gray-500 text-sm mt-4">
              Todas tus prendas están en rotación. Excelente optimización.
            </p>
          )}
        </div>
      </div>

      {/* Próximos looks */}
      {stats.proximosLooks.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-brand-gray-200">
          <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mb-4">
            Próximos looks planificados
          </h3>
          <div className="space-y-3">
            {stats.proximosLooks.map((look) => (
              <div
                key={look.id}
                className="flex items-center justify-between p-4 rounded-xl bg-brand-cream"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-sage/40 flex items-center justify-center text-brand-black">
                    <i className="ri-calendar-check-line" />
                  </div>
                  <div>
                    <p className="font-sans text-brand-black text-sm font-medium">
                      {look.titulo}
                    </p>
                    <p className="font-sans text-brand-gray-500 text-xs">
                      {new Date(look.fecha).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      {look.ocasion && ` · ${look.ocasion}`}
                    </p>
                  </div>
                </div>
                <span className="font-sans text-brand-gray-400 text-xs uppercase tracking-wider">
                  {look.prendas_ids.length} prendas
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
